import { useCallback, useEffect, useState } from "react";

import { orpcClient } from "@/lib/orpc-client";
import { useFeature } from "@/config/useFeature";
import { useIfcPreview } from "@/components/IfcViewerModal";
import { useTranslation } from "react-i18next";

const IFC_STATUS_DONE = "done";
const STATUS_CACHE_TTL_MS = 30_000;

type Marks = Array<string | null | undefined>;

const statusCache = new Map<string, { at: number; promise: Promise<boolean> }>();

export function resetProjectIfcStatusCache() {
  statusCache.clear();
}

function loadIfcReady(projectId: string): Promise<boolean> {
  const cached = statusCache.get(projectId);
  if (cached && Date.now() - cached.at < STATUS_CACHE_TTL_MS) {
    return cached.promise;
  }
  const promise = Promise.resolve(orpcClient.project.getIfc({ id: projectId }))
    .then((result: any) => result?.status === IFC_STATUS_DONE)
    .catch((error) => {
      console.error("Error loading ifc status:", error);
      statusCache.delete(projectId);
      return false;
    });
  statusCache.set(projectId, { at: Date.now(), promise });
  return promise;
}

export function assemblyMarks(row: any): Marks {
  return [
    row?.steelAssemblyCode?.toString(),
    row?.steelAssemblyId?.toString(),
  ];
}

export function useProjectIfc3d(projectId?: string | null, active = true) {
  const { t } = useTranslation();
  const enabled = useFeature("designBom3dView");
  const { openIfc, ifcPreview } = useIfcPreview();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!enabled || !projectId || !active) return;
    let cancelled = false;
    void loadIfcReady(projectId).then((ok) => {
      if (!cancelled) setReady(ok);
    });
    return () => {
      cancelled = true;
    };
  }, [enabled, projectId, active]);

  const open3d = useCallback(
    async (marks: Marks) => {
      if (!projectId || !ready) {
        openIfc(null, marks);
        return;
      }
      try {
        const result: any = await orpcClient.project.getIfc({ id: projectId });
        openIfc(
          result?.status === IFC_STATUS_DONE ? (result.url ?? null) : null,
          marks,
        );
      } catch (error) {
        console.error("Error loading ifc url:", error);
        openIfc(null, marks);
      }
    },
    [projectId, ready, openIfc],
  );

  const render3dCell = (marks: Marks) => (
    <a
      title={marks.find((mark) => mark?.trim())?.trim() || undefined}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        void open3d(marks);
      }}
      style={{
        color: ready ? "#1890ff" : "#8c8c8c",
        textDecoration: ready ? "underline" : "none",
        cursor: "pointer",
        whiteSpace: "nowrap",
      }}
    >
      {ready
        ? t("designBOM.view3d") || "3D 檢視"
        : t("designBOM.pick3dFile") || "選檔"}
    </a>
  );

  return {
    enabled,
    ready,
    title: t("designBOM.model3d") || "3D圖",
    open3d,
    render3dCell,
    ifcPreview: enabled ? ifcPreview : null,
  };
}
